import { Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EsgActionItem } from '../../../../../mocks/data/supplierEsgDashboardData';

const SEVERITY_COLOR: Record<EsgActionItem['severity'], 'info' | 'warning' | 'error'> = {
  Info: 'info',
  Warning: 'warning',
  Critical: 'error',
};

export interface EsgActionItemsTableProps {
  title: string;
  items: EsgActionItem[];
  emptyLabel?: string;
}

/** Reusable worklist table used across the Executive Overview and Risk & Actions tabs
 * (expiring certifications, expiring insurance, missing documents, overdue audits,
 * outstanding corrective actions, missing ESG questionnaires, compliance breaches - all
 * share the same {supplier, detail, due date, severity} shape). */
const EsgActionItemsTable: React.FC<EsgActionItemsTableProps> = ({ title, items, emptyLabel }) => {
  const { t } = useTranslation('esgDashboard');
  return (
    <Stack spacing={1}>
      <Typography variant="textSM"
        fontWeight={600}>{title} ({items.length})</Typography>
      {items.length === 0 ? (
        <Typography variant="textXS"
          color="text.secondary">{emptyLabel ?? t('actionItems.noneOutstanding', 'None outstanding.')}</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('common.supplier', 'Supplier')}</TableCell>
              <TableCell>{t('actionItems.detail', 'Detail')}</TableCell>
              <TableCell>{t('actionItems.due', 'Due')}</TableCell>
              <TableCell align="right">{t('actionItems.severity', 'Severity')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.supplierName}</TableCell>
                <TableCell>{item.detail}</TableCell>
                <TableCell>{item.dueDate ?? '-'}</TableCell>
                <TableCell align="right">
                  <Chip label={item.severity}
                    size="small"
                    color={SEVERITY_COLOR[item.severity]}
                    variant="outlined" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  );
};

export default EsgActionItemsTable;
